// A scenario in the file format the phones load (android/app/src/main/assets/scenarios/README.md).
// The server checks the same rules in private.scenario_problems before a scenario can
// go for review or be approved; `problems` here gives the editor the same list as you type.

export const LANGS = ["mr", "hi", "en"] as const;
export type Lang = (typeof LANGS)[number];
export const LANG_NAMES: Record<Lang, string> = { mr: "Marathi", hi: "Hindi", en: "English" };

export const ROLES = [
  "guard",
  "housekeeping",
  "guard-supervisor",
  "housekeeping-supervisor",
  "facility-manager",
] as const;
export type Role = (typeof ROLES)[number];
export const ROLE_NAMES: Record<Role, string> = {
  guard: "Guard",
  housekeeping: "Housekeeping",
  "guard-supervisor": "Guard supervisor",
  "housekeeping-supervisor": "Housekeeping supervisor",
  "facility-manager": "Facility manager",
};
export const roleName = (role: string) => ROLE_NAMES[role as Role] ?? role;

export const KEYS = ["A", "B", "C"] as const;
export type Key = (typeof KEYS)[number];
export const QUALITIES = ["best", "risky", "wrong"] as const;
export type Quality = (typeof QUALITIES)[number];
export const QUALITY_NAMES: Record<Quality, string> = { best: "Best", risky: "Risky", wrong: "Wrong" };

export type Status = "draft" | "in-review" | "approved" | "retired";
export const STATUS_NAMES: Record<Status, string> = {
  draft: "Draft",
  "in-review": "In review",
  approved: "Approved",
  retired: "Retired",
};

/** Pictures the phone app has. An unknown key shows no picture on the phone. */
export const ILLUSTRATIONS = [
  { key: "gate", name: "Society gate" },
  { key: "lift-lobby", name: "Lift lobby" },
] as const;

export interface SceneText {
  title: string;
  situation: string;
  situationAudio?: string;
  illustrationKey?: string;
}

export interface OptionText {
  text: string;
  audio?: string;
  outcome: string;
  outcomeAudio?: string;
  feedbackWentWell?: string[];
  keyRule?: string;
  sampleLine?: string;
  sampleLineAudio?: string;
}

export interface ScenarioOption {
  key: Key;
  quality: Quality;
  text: Record<Lang, OptionText>;
}

export interface ScenarioContent {
  id: string;
  version: number;
  sample?: boolean;
  roles: string[];
  skillTags: string[];
  estMinutes: number;
  text: Record<Lang, SceneText>;
  options: ScenarioOption[];
}

/** A scenario row as the web admin reads it from the database. */
export interface ScenarioRow {
  id: string;
  slug: string;
  status: Status;
  version: number;
  roles: string[];
  skill_tags: string[];
  content: ScenarioContent;
  sample: boolean;
  approved_at: string | null;
  updated_at: string;
  scenario_change: { status: "draft" | "in-review"; content: ScenarioContent; updated_at: string } | null;
  scenario_site: { site_id: string }[];
}

const byLang = <T>(make: () => T) => Object.fromEntries(LANGS.map((l) => [l, make()])) as Record<Lang, T>;

export function emptyScenario(): ScenarioContent {
  const quality: Record<Key, Quality> = { A: "best", B: "risky", C: "wrong" };
  return {
    id: "",
    version: 1,
    roles: [],
    skillTags: [],
    estMinutes: 3,
    text: byLang(() => ({ title: "", situation: "" })),
    options: KEYS.map((key) => ({ key, quality: quality[key], text: byLang(() => ({ text: "", outcome: "" })) })),
  };
}

/** Fills in anything an older or hand-made file leaves out, so the editor always has every field. */
export function normalise(content: Partial<ScenarioContent>): ScenarioContent {
  const empty = emptyScenario();
  const options = KEYS.map((key, i): ScenarioOption => {
    const found = content.options?.find((o) => o.key === key);
    return {
      key,
      quality: found?.quality ?? empty.options[i].quality,
      text: { ...empty.options[i].text, ...found?.text },
    };
  });
  return {
    ...empty,
    ...content,
    roles: content.roles ?? [],
    skillTags: content.skillTags ?? [],
    text: { ...empty.text, ...content.text },
    options,
  } as ScenarioContent;
}

const blank = (s: string | undefined | null) => !s || s.trim() === "";
const AUDIO = /^audio\/[A-Za-z0-9._/-]+\.m4a$/;
export const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** What stops the scenario from going for review, in the same words as the server. */
export function problems(c: ScenarioContent): string[] {
  const p: string[] = [];
  if (!SLUG.test(c.id ?? "")) p.push("The id must be lowercase words joined by hyphens.");
  if (typeof c.estMinutes !== "number" || !(c.estMinutes >= 1 && c.estMinutes <= 15)) {
    p.push("Minutes must be between 1 and 15.");
  }
  if (!c.roles?.length) p.push("Choose at least one role.");
  if (c.roles?.some((r) => !ROLES.includes(r as Role))) p.push("One of the roles is not known.");
  if (!c.skillTags?.length) p.push("Add at least one skill.");
  for (const lang of LANGS) {
    const t = c.text?.[lang];
    if (blank(t?.title)) p.push(`${LANG_NAMES[lang]} title is missing.`);
    if (blank(t?.situation)) p.push(`${LANG_NAMES[lang]} situation is missing.`);
  }
  const keys = c.options.map((o) => o.key).sort().join();
  if (keys !== "A,B,C") p.push("There must be exactly three responses, A, B and C.");
  const qualities = c.options.map((o) => o.quality).sort().join();
  if (qualities !== "best,risky,wrong") p.push("Mark one response best, one risky and one wrong.");
  for (const o of c.options) {
    for (const lang of LANGS) {
      const t = o.text?.[lang];
      const name = LANG_NAMES[lang];
      if (blank(t?.text)) p.push(`Response ${o.key}: ${name} text is missing.`);
      if (blank(t?.outcome)) p.push(`Response ${o.key}: ${name} outcome is missing.`);
      if (o.quality === "best") {
        if (blank(t?.keyRule)) p.push(`Response ${o.key}: ${name} rule to remember is missing.`);
        if (blank(t?.sampleLine)) p.push(`Response ${o.key}: ${name} "You can say" line is missing.`);
      }
    }
  }
  for (const path of audioPaths(c)) {
    if (!AUDIO.test(path)) p.push(`The audio file "${path}" is not an .m4a clip.`);
  }
  return p;
}

/** Whether a language has every text the phone needs. */
export function languageComplete(c: ScenarioContent, lang: Lang): boolean {
  const name = LANG_NAMES[lang];
  return !problems(c).some((p) => p.startsWith(name) || p.includes(`: ${name} `));
}

export function audioPaths(c: ScenarioContent): string[] {
  const paths: (string | undefined)[] = [];
  for (const t of Object.values(c.text ?? {})) paths.push(t?.situationAudio);
  for (const o of c.options ?? []) {
    for (const t of Object.values(o.text ?? {})) paths.push(t?.audio, t?.outcomeAudio, t?.sampleLineAudio);
  }
  return paths.filter((p): p is string => !!p);
}

/** The title to show in lists: English, else Marathi, else Hindi, else the id. */
export function titleOf(c: Pick<ScenarioContent, "text" | "id">): string {
  for (const lang of ["en", "mr", "hi"] as Lang[]) {
    const t = c.text?.[lang]?.title?.trim();
    if (t) return t;
  }
  return c.id || "Untitled scenario";
}

/** An id from an English title: "Argument at the gate" -> "argument-at-the-gate". */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/, "");
}

/** "visitor-disputes" -> "Visitor disputes". */
export function skillName(tag: string): string {
  const words = tag.replace(/-/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A skill tag from what someone typed: "Night patrol" -> "night-patrol". */
export function skillTag(typed: string): string {
  return slugify(typed);
}

/**
 * Tidies the content before saving: drops empty optional fields and blank "what went
 * well" lines, gives every language the same picture, and keeps the rule to remember and
 * the "You can say" line only on the best answer, where the phone reads them.
 */
export function tidy(c: ScenarioContent): ScenarioContent {
  const illustration = LANGS.map((l) => c.text[l]?.illustrationKey).find((k) => k);
  const text = Object.fromEntries(
    LANGS.map((l) => {
      const t = { ...c.text[l] };
      if (illustration) t.illustrationKey = illustration;
      else delete t.illustrationKey;
      if (!t.situationAudio) delete t.situationAudio;
      return [l, t];
    }),
  ) as Record<Lang, SceneText>;
  const options = c.options.map((o) => ({
    ...o,
    text: Object.fromEntries(
      LANGS.map((l) => {
        const t: OptionText = { ...o.text[l] };
        const wentWell = (t.feedbackWentWell ?? []).map((s) => s.trim()).filter(Boolean);
        if (wentWell.length) t.feedbackWentWell = wentWell;
        else delete t.feedbackWentWell;
        if (o.quality !== "best") {
          delete t.keyRule;
          delete t.sampleLine;
          delete t.sampleLineAudio;
        }
        for (const field of ["audio", "outcomeAudio", "keyRule", "sampleLine", "sampleLineAudio"] as const) {
          if (!t[field]) delete t[field];
        }
        return [l, t];
      }),
    ) as Record<Lang, OptionText>,
  }));
  const tidied: ScenarioContent = { ...c, text, options };
  if (!tidied.sample) delete tidied.sample;
  return tidied;
}
